// sideGameValidation.js — pure validation helpers for side games.
//
// Two use cases:
//   1. validateSideGameSelection — check the current create-form / staged
//      configuration and return a plain-language reason when it's not
//      startable. Powers the inline requirement line under the Start button
//      in StandaloneGamesPanel + PreRoundGamesPanel so a user never faces
//      a silent-disabled button (R1a of the sidegames-ux task).
//   2. invalidateStagedGames — walk the staged-game list and drop any game
//      whose format constraints no longer fit the current player count.
//      Powers PlayPage's auto-unstage-with-toast on player-count change (R3).
//
// No JSX, no React imports — everything is pure so it's directly unit-testable
// without jsdom (this repo's vitest suite has no jsdom / @testing-library, per
// the scout brief).

// Format requirements — kept here alongside the validators (rather than
// imported from FORMAT_OPTIONS / FORMAT_CATALOG in the two panels) so a test
// file can pull in this module without dragging in JSX-heavy panels.
const FORMAT_REQS = {
  skins:        { min: 2, max: null, teamed: false, wolf: false, vegas: false, label: 'Skins' },
  nassau:       { min: 2, max: null, teamed: true,  wolf: false, vegas: false, label: 'Nassau' },
  stableford:   { min: 2, max: null, teamed: false, wolf: false, vegas: false, label: 'Stableford' },
  simple:       { min: 2, max: null, teamed: false, wolf: false, vegas: false, label: 'Simple' },
  match_1v1:    { min: 2, max: null, teamed: true,  wolf: false, vegas: false, label: 'Match Play' },
  wolf:         { min: 3, max: null, teamed: false, wolf: true,  vegas: false, label: 'Wolf' },
  vegas:        { min: 4, max: 4,    teamed: true,  wolf: false, vegas: true,  label: 'Vegas' },
  shuffle_card: { min: 2, max: null, teamed: false, wolf: false, vegas: false, label: 'Shuffle Card' },
}

export function getFormatReqs(format) {
  return FORMAT_REQS[format] || null
}

// Compute the {a,b} team counts the pre-round staging UI will actually ship
// for a non-Vegas teamed format (Nassau, Match Play). PreRoundGamesPanel has
// no A/B assignment UI for these — toCreateGamePayload auto-splits by seat
// (seat 0=A, seat 1=B, alternating). validateSideGameSelection's
// `req.teamed && selectedCount > 2` branch checks teamCounts, so callers must
// supply the actual split rather than {a:0,b:0} (which would trip a false
// "Assign every player to Team A or Team B" error the user cannot act on).
//
// For any playerCount >= 2 this returns non-zero counts on both teams, so the
// teamed check trivially passes — mirroring what will actually be created.
export function preRoundAutoTeamCounts(playerCount) {
  const n = Math.max(0, Number.isFinite(playerCount) ? playerCount : 0)
  return {
    a: Math.ceil(n / 2),
    b: Math.floor(n / 2),
  }
}

// Validate an active create-form / staged selection. Returns
// { valid: true } on success or { valid: false, reason: <plain-language> }
// on failure. Callers render `reason` as an inline requirement line and
// also gate the Start button on `!valid`.
//
// Inputs:
//   format            — 'skins' | 'nassau' | … one of FORMAT_REQS keys
//   selectedCount     — number of players in the selection
//   teamCounts        — { a, b } A/B counts (defaulted to 0/0)
//   vegasTeamsMode    — 'same' | 'switch6' (default 'same')
//   wolfRotationMode  — 'scorecard' | 'set' | 'auto' (default 'scorecard')
//   wolfOrderLen      — length of the manual rotation order (only for
//                       wolfRotationMode='set')
export function validateSideGameSelection({
  format,
  selectedCount = 0,
  teamCounts = { a: 0, b: 0 },
  vegasTeamsMode = 'same',
  wolfRotationMode = 'scorecard',
  wolfOrderLen = 0,
} = {}) {
  const req = FORMAT_REQS[format]
  if (!req) return { valid: false, reason: 'Pick a game format first.' }

  // Fixed-count formats (Vegas today: exactly 4).
  if (req.max != null && selectedCount !== req.max) {
    if (selectedCount < req.max) {
      const need = req.max - selectedCount
      return {
        valid: false,
        reason: `${req.label} needs exactly ${req.max} players — ${selectedCount} selected, add ${need} more.`,
      }
    }
    return {
      valid: false,
      reason: `${req.label} needs exactly ${req.max} players — ${selectedCount} selected, remove ${selectedCount - req.max}.`,
    }
  }

  // Below-min for flexible-count formats.
  if (selectedCount < req.min) {
    return {
      valid: false,
      reason: `${req.label} needs at least ${req.min} players — ${selectedCount} selected.`,
    }
  }

  if (req.vegas) {
    if (vegasTeamsMode === 'switch6') {
      // Server seeds the 2v2 from a deterministic rotation; no manual split
      // needed. Fully valid once selectedCount hits the fixed-max above.
      return { valid: true }
    }
    if (!(teamCounts.a === 2 && teamCounts.b === 2)) {
      return {
        valid: false,
        reason: `Assign 2 players to Team A and 2 to Team B (A:${teamCounts.a} · B:${teamCounts.b}).`,
      }
    }
    return { valid: true }
  }

  if (req.teamed && selectedCount > 2) {
    if (teamCounts.a === 0 || teamCounts.b === 0) {
      return {
        valid: false,
        reason: `Assign every player to Team A or Team B (A:${teamCounts.a} · B:${teamCounts.b}).`,
      }
    }
  }

  if (req.wolf && wolfRotationMode === 'set' && wolfOrderLen !== selectedCount) {
    return {
      valid: false,
      reason: 'Set the Wolf rotation order for every player.',
    }
  }

  return { valid: true }
}

// Walk the staged-game list and drop any game whose format no longer fits
// the current player count (e.g. Vegas after a 4→3 change, Wolf after a
// 3→2 change). Also reconciles Vegas seat-team assignments (`team_seats`)
// and Wolf staged rotation-order (seat-index array) so games that remain
// valid don't carry stale seat references.
//
// Returns { kept, removed:[{format, label, reason}] }.
export function invalidateStagedGames(stagedGames, playerCount) {
  const kept = []
  const removed = []
  for (const sg of stagedGames || []) {
    const req = FORMAT_REQS[sg.format]
    if (!req) {
      // Unknown format — keep as-is; a future format shouldn't be silently
      // clobbered by an older invalidator.
      kept.push(sg)
      continue
    }
    // Fixed-count formats (Vegas: exactly 4) — check the exact-count message
    // FIRST so a 3-player group hitting a max=4 rule gets "needs exactly 4"
    // rather than the more generic "at least 4" phrasing (both are true but
    // the exact-count wording matches what the user has to fix).
    if (req.max != null && playerCount !== req.max) {
      removed.push({
        format: sg.format,
        label: req.label,
        reason: `${req.label} removed — needs exactly ${req.max} players (group is ${playerCount} now).`,
      })
      continue
    }
    if (playerCount < req.min) {
      removed.push({
        format: sg.format,
        label: req.label,
        reason: `${req.label} removed — needs at least ${req.min} players (group is ${playerCount} now).`,
      })
      continue
    }
    // Reconcile Vegas seat-team assignments — trim to playerCount and pad
    // with a sensible default if the seats array is short.
    if (req.vegas) {
      const seats = Array.isArray(sg.config?.team_seats) ? [...sg.config.team_seats] : ['A', 'A', 'B', 'B']
      const trimmed = seats.slice(0, playerCount)
      while (trimmed.length < playerCount) trimmed.push(trimmed.length < 2 ? 'A' : 'B')
      kept.push({ ...sg, config: { ...sg.config, team_seats: trimmed } })
      continue
    }
    // Reconcile Wolf's staged rotation-order (seat indices). Drop any index
    // that would point past the current player count; the payload builder
    // fills the remainder from the natural seat order.
    if (req.wolf && Array.isArray(sg.config?.rotation_order_seats)) {
      const ro = sg.config.rotation_order_seats.filter(seat => Number.isInteger(seat) && seat >= 0 && seat < playerCount)
      kept.push({ ...sg, config: { ...sg.config, rotation_order_seats: ro } })
      continue
    }
    kept.push(sg)
  }
  return { kept, removed }
}
